import React from 'react';
import '../../../styles.scss';
import {
  OwcTypography, OwcExpandable, OwcInput, OwcButton, OwcRadio,
  OwcList, OwcListItem, OwcPagination, OwcModalDialog,
  OwcTable, OwcTableHeader, OwcTableHeaderCell, OwcTableBody,
   OwcTableRow, OwcTableCell,
  OwcTabs, OwcTab,
  OwcSwitch, OwcProgressSpinner,
} from '@one/react';
import { configData } from '../../../config.js';
import { fetchSigned, parseDate, checkForCompletion } from '../../../shared/Utilities.js';
import SimpleDateInput from '../../general/SimpleDateInput.js';
import CountrySelector from '../../general/CountrySelector.js';
import ReferenceListSelector from '../../general/ReferenceListSelector.js';
import AgreementSelection from '../../useCase/AgreementSelection.js';
import { Chart } from "react-google-charts";


/**
 * @copyright Roche 2025
 */

/**
 * Component for the Coverage with Data Licenses data product.
 */
class ManagementReport extends React.Component {

  constructor(props) {
    super(props);

    this.cancelRequested = React.createRef();
    this.cancelRequested.current = false;

    this.state = {
      reportResults: null,
      reportSpecName: null,
      reportSpecId: null,
      isGenerating: false,
      userMessage: null,
      label: {},
      overviewExpanded: true,
      specificationsExpanded: true,
      resultsExpanded: true,
      specifications: [],
      filteredSpecifications: [],
      searchTerm: "",
      selectedSpecId: null,
      specPage: 1,
      specRowsPerPage: 10,
      currentView: "main",
      editingSpec: null,
      specEditorExpanded: true,
      lastUploadSinceError: "",
      deviceDetailFilter: "",
      deviceDetailList: ["Serial No", "Lab ID", "Account Number", "Instrument Location"],
      filterDeviceDetailsBy: "Serial No",
      lastUploadSince: null,
      selectedCountryCodes: [],
      countrySelectorVisible: false,
      systemTypeList: [],
      selectedSystemTypes: [],
      acceptedAgreementList: [],
      declinedAgreementList: [],
      nonRespondingAgreementList: [],
      resetAgreementControl: false,
      devicesGeoGroupBy: "country",
      devicesGeoViewTab: "chart",
      locationAccountsGeoGroupBy: "country",
      locationAccountsGeoViewTab: "chart",
      devicesInstGroupBy: "line",
      devicesInstViewTab: "chart",
      locationAccountsInstGroupBy: "line",
      locationAccountsInstViewTab: "chart",
    };
  }

  static getDataProductName() {
    return "Coverage with Data Licenses";
  }

  componentDidMount() {
    this.loadLabel();
    this.loadSpecifications();
    this.loadSystemTypes();
  }

  loadSystemTypes() {
    fetchSigned(configData.REFDATA_API_URL + "?includeInactive=true")
      .then(res => res.json())
      .then(
        (result) => {
          const filteredSystemTypeList = result.filter(value =>
            value.type === "Reference System Type"
          );
          this.setState({ systemTypeList: filteredSystemTypeList });
        },
        (error) => { this.setState({ error }); }
      );
  }

  loadLabel() {
    const baseUrl = this.props.baseUrl;
    if (baseUrl !== null && baseUrl !== undefined) {
      const labelUrl = baseUrl + "label/";
      fetchSigned(labelUrl)
        .then(async response => {
          let responseDict = {};
          if (response !== undefined) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
              responseDict = await response.json();
            } else {
              console.log(response);
            }
          }
          this.setState({ label: responseDict });
        })
        .catch(error => {
          this.setState({ responseText: error.toString() });
          console.error('There was an error!', error);
        });
    }
  }

  loadSpecifications() {
    const url = configData.DATA_PRODUCT_URL
      + "/data/spec/list_dp_spec/?dataProductName="
      + encodeURIComponent(ManagementReport.getDataProductName());

    fetchSigned(url)
      .then(async response => {
        let specList = [];
        if (response !== undefined && response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            specList = await response.json();
          }
        }
         const { specPage, specRowsPerPage } = this.state;
        const totalPages = Math.ceil(specList.length / specRowsPerPage);
        const adjustedPage = specPage > totalPages ? Math.max(1, totalPages) : specPage;
        this.setState({
          specifications: specList,
          filteredSpecifications: specList,
          specPage: adjustedPage,
        });
      })
      .catch(error => {
        console.error('Error loading specifications:', error);
      });
  }

  getDescriptionFromLabel() {
    const { label } = this.state;
    if (!label || Object.keys(label).length === 0) return null;

    const attrs =
      label?.[configData.LABEL_KEYS.DATA_PRODUCT_ATTRIBUTES_KEY]?.[
        configData.LABEL_KEYS.CHILDREN_KEY
      ];

    return (
      attrs?.[configData.LABEL_KEYS.DESCRIPTION_KEY]?.[
        configData.LABEL_KEYS.VALUE_KEY
      ] || null
    );
  }

  handleSearch() {
    const searchTerm = this.state.searchTerm || "";
    const filtered = searchTerm.trim() === ""
      ? this.state.specifications
      : this.state.specifications.filter(spec =>
          (spec.specificationName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (spec.createdBy || "").toLowerCase().includes(searchTerm.toLowerCase())
        );

    this.setState({
      filteredSpecifications: filtered,
      specPage: 1,
    });
  }

  handleSpecSelect(specId) {
    this.setState({ selectedSpecId: specId });
  }

  handlePageChange(ev) {
    const newPage = ev.detail.page;
    const newPageSize = ev.detail.pageSize;
    const stateChanges = {};
    if (newPageSize !== this.state.specRowsPerPage) {
      stateChanges.specRowsPerPage = newPageSize;
      stateChanges.specPage = 1;
    }
    if (newPage !== this.state.specPage) {
      stateChanges.specPage = newPage;
    }
    if (Object.keys(stateChanges).length > 0) {
      this.setState(stateChanges);
    }
  }

  handleNewSpec() {
    this.setState({
      currentView: "editSpec",
      editingSpec: {
        specificationId: null,
        specificationName: "",
        dataProductName: ManagementReport.getDataProductName(),
      },
      specEditorExpanded: true,
      selectedCountryCodes: [],
      lastUploadSince: null,
      selectedSystemTypes: [],
      deviceDetailFilter: "",
      filterDeviceDetailsBy: "Serial No",
      acceptedAgreementList: [],
      declinedAgreementList: [],
      nonRespondingAgreementList: [],
      resetAgreementControl: !this.state.resetAgreementControl,
    });
  }

  async fetchAgreementsByIds(idString) {
    if (!idString || idString.trim() === "") return [];
    const searchData = {
      searchBy: "Agreement ID",
      searchTerm: idString,
    };
    try {
      const response = await fetchSigned(configData.USECASE_API_URL + "agreement-search/", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(searchData),
      });
      if (response === undefined || !response.ok) {
        console.error("Error fetching agreement details for ids:", idString);
        return [];
      }
      const results = await response.json();
      return Array.isArray(results) ? results : [];
    } catch (error) {
      console.error("Error fetching agreement details:", error);
      return [];
    }
  }

  handleEditSpec() {
    const specId = this.state.selectedSpecId;
    if (specId === null) return;

    const url = configData.DATA_PRODUCT_URL
      + "/data/spec/get_dp_spec/" + specId;

    fetchSigned(url)
      .then(async response => {
        if (response !== undefined && response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            const specData = await response.json();
            const spec = Array.isArray(specData) && specData.length > 0 ? specData[0] : specData;

            // Parse the specification JSON to populate editor fields
            let parsedSpec = {};
            if (spec.specification) {
              try {
                  let specValue = spec.specification;
                // Handle double-encoded JSON strings
                if (typeof specValue === "string") {
                  specValue = JSON.parse(specValue);
                }
                if (typeof specValue === "string") {
                  specValue = JSON.parse(specValue);
                }
                parsedSpec = specValue;
              } catch (e) {
                console.error("Error parsing specification:", e);
              }
            }

            const countryCodes = parsedSpec.country
              ? parsedSpec.country.split(",").map(c => c.trim()).filter(c => c)
              : [];
            const systemTypes = parsedSpec.referenceSystemType
              ? parsedSpec.referenceSystemType.split(",")
                  .map(s => Number(s.trim()))
                  .filter(n => !Number.isNaN(n))
              : [];
            const [acceptedIds, declinedIds, nonRespondingIds] = await Promise.all([
              this.fetchAgreementsByIds(parsedSpec.acceptedAgreementIds),
              this.fetchAgreementsByIds(parsedSpec.declinedAgreementIds),
              this.fetchAgreementsByIds(parsedSpec.nonRespondingAgreementIds),
            ]);

            this.setState({
              currentView: "editSpec",
              editingSpec: spec,
              specEditorExpanded: true,
              selectedCountryCodes: countryCodes,
              lastUploadSince: parsedSpec.lastUploadSince || null,
              selectedSystemTypes: systemTypes,
              acceptedAgreementList: acceptedIds,
              declinedAgreementList: declinedIds,
              nonRespondingAgreementList: nonRespondingIds,
              resetAgreementControl: !this.state.resetAgreementControl,
            });
          }
        }
      })
      .catch(error => {
        console.error('Error loading specification:', error);
      });
  }

  handleBackToMain() {
    this.setState({
      currentView: "main",
      editingSpec: null,
    });
  }

  buildSpecificationPayload() {
    const spec = this.state.editingSpec;
    const specification = JSON.stringify({
      country: this.state.selectedCountryCodes.join(","),
      lastUploadSince: this.state.lastUploadSince || "",
      referenceSystemType: this.state.selectedSystemTypes.join(","),
      acceptedAgreementIds: this.state.acceptedAgreementList
        .map(a => a.customerAgreementId).join(","),
      declinedAgreementIds: this.state.declinedAgreementList
        .map(a => a.customerAgreementId).join(","),
      nonRespondingAgreementIds: this.state.nonRespondingAgreementList
        .map(a => a.customerAgreementId).join(","),
    });

    return {
      specificationId: spec.specificationId === null ? "-1" : spec.specificationId,
      specificationName: spec.specificationName || "",
      dataProductName: ManagementReport.getDataProductName(),
      userName: this.props.userName || "",
      specification: specification,
    };
  }

  handleSaveSpec() {
    const spec = this.state.editingSpec;
    if (!spec.specificationName || spec.specificationName.trim() === "") {
      this.setState({
        userMessage: { message: "Report name is required", type: "error" },
      });
      return;
    }
    if (this.state.acceptedAgreementList.length === 0) {
      this.setState({
        userMessage: { message: "At least one accepted agreement is required", type: "error" },
      });
      return;
    }
    const payload = this.buildSpecificationPayload();
    const url = configData.DATA_PRODUCT_URL + "/data/spec/save_dp_spec/";

    fetchSigned(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async response => {
        if (response !== undefined && response.ok) {
           this.setState({
            currentView: "main",
            editingSpec: null,
          });
        }
        // Refresh the spec list
        this.loadSpecifications();
      })
      .catch(error => {
        console.error('Error saving specification:', error);
        this.setState({
          userMessage: { message: "Error saving specification: " + error.toString(), type: "error" },
        });
      });
  }

  handleDeleteSpec() {
    const specId = this.state.selectedSpecId;
    if (specId === null) return;

    const url = configData.DATA_PRODUCT_URL
      + "/data/spec/delete_dp_spec/" + specId;

    fetchSigned(url)
      .then(() => {
        this.setState({ selectedSpecId: null });
        this.loadSpecifications();
      })
      .catch(error => {
        console.error('Error deleting specification:', error);
      });
  }

  async handleGenerateReport() {
    const specId = this.state.selectedSpecId;
    if (specId === null) return;

    const baseUrl = this.props.baseUrl;
    if (!baseUrl) return;

    this.cancelRequested.current = false;
    this.setState({ reportResults: null, reportSpecName: null, reportSpecId: null, isGenerating: true, resultsExpanded: true, userMessage: { message: "Requesting Data Product...", type: "info" } });

    try {
      // Load the spec to get its parameters
      const specUrl = configData.DATA_PRODUCT_URL
        + "/data/spec/get_dp_spec/" + specId;

      const response = await fetchSigned(specUrl);
      if (response === undefined || !response.ok) {
        this.setState({ userMessage: { message: "Error loading specification", type: "error" } });
        return;
      }

      const specData = await response.json();
      const spec = Array.isArray(specData) && specData.length > 0 ? specData[0] : specData;

      let parsedSpec = {};
      if (spec.specification) {
        try {
          let specValue = spec.specification;
          if (typeof specValue === "string") specValue = JSON.parse(specValue);
          if (typeof specValue === "string") specValue = JSON.parse(specValue);
          parsedSpec = specValue;
        } catch (e) {
          console.error("Error parsing specification:", e);
        }
      }
      console.log("Parsed specification for report:", parsedSpec);

      // Build query parameters from the specification
      const params = [];
      if (parsedSpec.country) params.push(`country=${parsedSpec.country}`);
      if (parsedSpec.lastUploadSince) params.push(`lastUploadSince=${parsedSpec.lastUploadSince}`);
      if (parsedSpec.referenceSystemType) params.push(`referenceSystemType=${parsedSpec.referenceSystemType}`);
      if (parsedSpec.acceptedAgreementIds) params.push(`acceptedAgreementIds=${parsedSpec.acceptedAgreementIds}`);
      if (parsedSpec.declinedAgreementIds) params.push(`declinedAgreementIds=${parsedSpec.declinedAgreementIds}`);
      if (parsedSpec.nonRespondingAgreementIds) params.push(`nonRespondingAgreementIds=${parsedSpec.nonRespondingAgreementIds}`);

      let reportUrl = baseUrl + (params.length > 0 ? "?" + params.join("&") : "");

      // Add userName for tracking
      if (this.props.userName) {
        reportUrl += (params.length > 0 ? "&" : "?") + `userName=${this.props.userName}`;
      }
      console.log("Generate report URL:", reportUrl);

      // Submit the request â returns an execution ARN
      this.setState({ userMessage: { message: "Requesting Data Product...", type: "info" } });
      const submitResponse = await fetchSigned(reportUrl);
      if (submitResponse === undefined || !submitResponse.ok) {
        this.setState({ userMessage: { message: "Error requesting data product", type: "error" } });
        return;
      }

      const executionArn = await submitResponse.json();
      console.log("Execution ARN:", executionArn);

      // Poll for completion
      this.setState({ userMessage: { message: "Generating Data Product...", type: "info" } });
      const statusUrl = baseUrl + "status/" + executionArn;
      const requestResult = await checkForCompletion(statusUrl, this.cancelRequested);

      if (requestResult.status === "SUCCEEDED") {
        // Fetch the actual results from the result URL
        this.setState({ userMessage: { message: "Reading Data Product...", type: "info" } });
        const resultsResponse = await fetch(requestResult.resultUrl, {
          method: "GET",
          headers: { 'Accept': '*/*' },
          mode: "cors",
          cache: "no-cache",
          referrer: "client",
        });

        if (resultsResponse.ok) {
          const reportResults = await resultsResponse.json();
          this.setState({
            reportResults,
            reportSpecName: spec.specificationName || "",
            reportSpecId: spec.specificationId,
            resultsExpanded: true,
            userMessage: null,
          });
        } else {
          this.setState({ userMessage: { message: "Error reading data product results", type: "error" } });
        }
      } else {
        this.setState({
          userMessage: { message: requestResult.responseText || "Data product generation failed", type: "error" },
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      this.setState({
        userMessage: { message: "Error generating report: " + error.toString(), type: "error" },
      });
    } finally {
      this.setState({ isGenerating: false });
    }
  }
  renderDeviceFilterSection() {
    return (
      <div style={{
        display: "flex", gap: "1em", alignItems: "flex-start", flexDirection: "row",
        backgroundColor: "var(--one-color-background-intensity-low)", padding: "0.5em",
        width: "100%", boxSizing: "border-box",
      }}>
        <OwcTypography variant="title6" style={{ whiteSpace: "nowrap", paddingTop: "0.3em" }}>
          Device Filters
        </OwcTypography>
      </div>
    );
  }

  renderDevicesToInclude() {
    return (
      <div style={{ marginTop: "0.5em" }}>
        <OwcTypography variant="title6">Devices to Include</OwcTypography>
      </div>
    );
  }

  renderLastUploadSince() {
    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "1em", marginTop: "0.5em" }}>
        <OwcTypography style={{ paddingTop: "0.8em" }}>Last Upload Since</OwcTypography>
        <SimpleDateInput label="Date" value={this.state.lastUploadSince}
          placeholder="Enter date"
           onValueChange={(ev) => {
            if (!ev) {
              this.setState({ lastUploadSince: null, lastUploadSinceError: "" });
              return;
            }
            const parsed = parseDate(ev);
            if (parsed !== null && parsed > new Date()) {
              this.setState({ lastUploadSinceError: "Date must not be in the future" });
              return;
            }
            this.setState({ lastUploadSince: ev, lastUploadSinceError: "" });
          }}
          showExampleDate={true}>
        </SimpleDateInput>
         {this.state.lastUploadSinceError &&
          <OwcTypography style={{ color: "var(--one-color-red-500)", fontSize: "0.8em", marginTop: "0.2em" }}>
            {this.state.lastUploadSinceError}
          </OwcTypography>
        }
      </div>
    );
  }

  renderCountryFilter() {
    const countryCount = this.state.selectedCountryCodes.length;
    return (
      <div style={{ marginTop: "0.5em",marginRight:'16px' }}>
        <OwcTypography variant="title6">Country Filter</OwcTypography>
        <div style={{ display: "flex", flexDirection: "column", marginTop: "0.3em" }}>
          <OwcButton onclick={() => this.setState({ countrySelectorVisible: true })}>
            Click to select countries
          </OwcButton>
          <OwcInput
            cols="80"
            rows="3"
            label={countryCount === 0
              ? "No countries selected - The report will be global"
              : `${countryCount} ${countryCount === 1 ? "country" : "countries"} selected`
            }
            value={this.state.selectedCountryCodes}
            type="textarea"
            style={{ marginTop: "0.5em" }}
            no-clear-icon resizable="false" disabled>
          </OwcInput>
        </div>
      </div>
    );
  }

  renderCountrySelectionDialog() {
    const modalStyle = { maxWidth: '950px', maxHeight: '650px' };
    return (
      <OwcModalDialog disableBackdropClick disableEscapeKeydown
          disableScrollLock="false" hideBackdrop="false" size="large"
          cardProps={{ style: modalStyle }}
          visible={this.state.countrySelectorVisible}>
        <div style={{ justifyContent: 'flex-end', padding: '10px' }}>
          <CountrySelector
            title="Countries"
            selectedCountryCodes={this.state.selectedCountryCodes}
            onSelectedChange={(selectedCountryCodes) => this.setState({ selectedCountryCodes })} />
        </div>
        <div slot="header" style={{ justifyContent: 'flex-end', padding: '10px' }}>
          <OwcTypography variant="title6">
            Select the desired countries
          </OwcTypography>
        </div>
        <div slot="footer" style={{ justifyContent: 'flex-end', padding: '5px' }}>
          <OwcButton style={{ width: "fit-content" }} onclick={() => this.setState({ countrySelectorVisible: false })}>
            Close
          </OwcButton>
        </div>
      </OwcModalDialog>
    );
  }

  renderSystemTypeFilter() {
    const refListData = this.state.systemTypeList;
    const selectedItems = this.state.selectedSystemTypes;
    const availableIds = new Set(refListData.map(item => item.refListId));
    const selectedCount = selectedItems.filter(id => availableIds.has(id)).length;
    return (
      <OwcTable style={{ marginBottom: "0.5em",width:'95%',paddingLeft:'1em'}} size="dense" height="auto" >
        <OwcTableHeader elevated sticky>
          <OwcTableHeaderCell width="90%" resizable>
            <OwcTypography variant="title6">
              {`Reference System Type Filter - ${selectedCount===0?"None Selected":`${selectedCount} Selected`}`}
            </OwcTypography>
          </OwcTableHeaderCell>
        </OwcTableHeader>
        <OwcTableBody>
          <div style={{ marginLeft: "1em", marginRight: "1em", marginBottom: "1em" }}>
            <ReferenceListSelector
              size="small"
              refListData={refListData}
              selectedItems={selectedItems}
              aggregateSublist
              onSelectedChange={(selectedItems)=>this.setState({selectedSystemTypes: selectedItems})} />
          </div>
        </OwcTableBody>
      </OwcTable>
    );
  }

  renderAgreementGroup(title, listKey, defaultSearchTerm) {
    const agreementList = this.state[listKey];
    const isNewSpec = this.state.editingSpec && this.state.editingSpec.specificationId === null;
    return (
      <div style={{ marginTop: "0.5em" }}>
        <OwcTypography variant="title6">{title}</OwcTypography>
        <div style={{ marginTop: "0.5em", marginLeft: "1em", marginRight: "1em" }}>
          <AgreementSelection selectedList={agreementList}
            resetSearch={this.state.resetAgreementControl}
            searchOnStart={true}
            defaultSearchBy="Agreement Title"
            defaultSearchTerm={isNewSpec ? defaultSearchTerm : ""}
            onRemoveSelectedItem={(agreementToRemove) => {
              const newList = agreementList.filter(
                agreement => agreement.customerAgreementId !== agreementToRemove.customerAgreementId
              );
              this.setState({ [listKey]: newList });
            }}
            onAddSelectedItem={(agreement) => {
              const newList = [...this.state[listKey]];
              newList.push(agreement);
              this.setState({ [listKey]: newList });
            }}
          />
        </div>
      </div>
    );
  }

  renderAgreementSection() {
    return (
      <div style={{ marginTop: "1em" }}>
        <div style={{
          backgroundColor: "var(--one-color-background-intensity-low)", padding: "0.5em",
          width: "100%", boxSizing: "border-box",
        }}>
          <OwcTypography variant="title6">Agreement Specification</OwcTypography>
        </div>
        {this.renderAgreementGroup("Accepted Agreements Defined By", "acceptedAgreementList", "accepted")}
        {this.renderAgreementGroup("Declined Agreements Defined By", "declinedAgreementList", "declined")}
        {this.renderAgreementGroup("Non Responding Agreements Defined By", "nonRespondingAgreementList", "non responding")}
      </div>
    );
  }

  renderSpecEditorView() {
    const spec = this.state.editingSpec;
    if (spec === null) return "";

    const specName = spec.specificationName || "";
     const specId = spec.specificationId;
    const titleText = specId !== null && specId !== "-1"
      ? `Report Specification - ${specId}`
      : "Report Specification";

    const missingRequirements = [];
    if (!specName || specName.trim() === "") missingRequirements.push("a report name");
    if (this.state.acceptedAgreementList.length === 0) missingRequirements.push("at least one accepted agreement");
    const saveDisabled = missingRequirements.length > 0;
    const saveDisabledMessage = saveDisabled ? "To save, please provide " + missingRequirements.join(" and ") + "." : "";

    return (
      <div style={{ marginLeft: "2em", marginRight: "2em" }}>
        <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
            expanded={this.state.specEditorExpanded}
            onExpandedChange={(ev) => { this.setState({ specEditorExpanded: ev.detail }) }}>
          <span slot="title">
            <OwcTypography variant="title6">
              {titleText}
            </OwcTypography>
          </span>
          <span slot="content">
            <div style={{ padding: '0.3rem 3rem' }}>
              {/* Report Name + Buttons row */}
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1em' }}>
                  <OwcTypography variant="title6">Report Name</OwcTypography>
                  <OwcInput
                    style={{ minWidth: "20em" }}
                    variant="outlined"
                    placeholder="Enter report name"
                    value={specName}
                    onValueChange={(ev) => {
                      this.setState({
                        editingSpec: { ...this.state.editingSpec, specificationName: ev.detail }
                      });
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
                  <OwcButton disabled={saveDisabled}
                      onclick={() => this.handleSaveSpec()}>
                    Save Specification
                  </OwcButton>
                  {saveDisabled &&
                    <OwcTypography variant="caption"
                        style={{ color: "var(--one-color-interaction-default-negative-1)", maxWidth: "20em" }}>
                      {saveDisabledMessage}
                    </OwcTypography>
                  }
                  <OwcButton onclick={() => this.handleBackToMain()}>
                    Close Without Saving
                  </OwcButton>
                </div>
              </div>

              {/* Device Filter - full width */}
              <div style={{ marginTop: "1em" }}>
                {this.renderDeviceFilterSection()}
              </div>

              {/* Left half + Right half */}
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent:'space-between', marginTop: '0.5em' }}>
                <div>
                  {this.renderDevicesToInclude()}
                  {this.renderLastUploadSince()}
                  {this.renderCountryFilter()}
                </div>
                <div>
                  {this.renderSystemTypeFilter()}
                </div>
              </div>

              {/* Agreement Specification - full width */}
              {this.renderAgreementSection()}
            </div>
          </span>
        </OwcExpandable>
        {this.renderCountrySelectionDialog()}
      </div>
    );
  }

  renderDescriptionSection() {
    const description = this.getDescriptionFromLabel();

    return (
      <table width="85%" cellSpacing="0">
        <tbody>
          {description !== null
            ? <>
                <tr>
                  <td width="15%" valign='top'
                      style={{backgroundColor:"var(--one-color-background-intensity-low)"}}>
                    <OwcTypography variant="title6" style={{paddingLeft: "0.5em"}}>Description</OwcTypography>
                  </td>
                  <td style={{paddingBottom: "0.5em", paddingLeft:"0.5em",
                              border:"1px solid var(--one-color-background-intensity-low)"}}>
                    <OwcTypography>{description}</OwcTypography>
                  </td>
                </tr>
              </>
            : ""
          }
        </tbody>
      </table>
    );
  }

  renderSearchSection() {
    return (
      <table width="100%" cellSpacing="0">
        <tbody>
          <tr>
            <td width="15%" valign='top'
                style={{backgroundColor:"var(--one-color-background-intensity-low)"}}>
              <OwcTypography variant="title6" style={{paddingLeft: "0.5em"}}>Search</OwcTypography>
            </td>
            <td style={{paddingBottom: "0.5em", paddingLeft:"0.5em",
                        border:"1px solid var(--one-color-background-intensity-low)"}}>
              <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "0.5em"}}>
                <OwcInput
                  style={{ flex: 1 }}
                  placeholder="Enter specification name or creator"
                  round={false}
                  variant="filled"
                  value={this.state.searchTerm}
                  onValueChange={(ev) => {
                    const newValue = ev.detail;
                    this.setState({ searchTerm: newValue }, () => {
                      if (newValue === "" || newValue === null || newValue === undefined) {
                        this.handleSearch();
                      }
                    });
                  }}
                  onClearClick={() => {
                    this.setState({ searchTerm: "" }, () => this.handleSearch());
                  }}
                  onKeyUp={(ev) => {
                    if (ev.key === "Enter") {
                      ev.preventDefault();
                      this.handleSearch();
                    }
                  }}
                />
                <OwcButton style={{ height: "3em" }} elevated
                    onClick={() => this.handleSearch()}>
                  Search
                </OwcButton>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

 renderSpecificationList() {
    const { filteredSpecifications, specPage, specRowsPerPage, selectedSpecId } = this.state;
    const totalPages = Math.ceil(filteredSpecifications.length / specRowsPerPage);
    const startIndex = (specPage - 1) * specRowsPerPage;
    const endIndex = Math.min(startIndex + specRowsPerPage, filteredSpecifications.length);
    const pageItems = filteredSpecifications.slice(startIndex, endIndex);

    return (
      <div style={{display:"flex", flexDirection:"column"}}>
        {pageItems.length > 0
          ? <OwcList size="medium">
              <div slot="title">Saved Specifications</div>
              {pageItems.map(spec => {
                const specId = spec.specificationId;
                const itemId = "specListItem" + specId;
                return (
                  <OwcListItem
                    key={itemId}
                    value={specId}
                    onClick={() => this.handleSpecSelect(specId)}
                  >
                    <div slot="prefix">
                      <OwcRadio
                        checked={selectedSpecId === specId}
                      />
                    </div>
                    <OwcTypography>{spec.specificationName || "â"}</OwcTypography>
                  </OwcListItem>
                );
              })}
            </OwcList>
          : <OwcTypography style={{marginLeft:"1em"}}>No Specifications</OwcTypography>
        }
        {totalPages > 1
          ? <OwcPagination page={specPage} pageSize={specRowsPerPage}
                hidePageSize={false} total={totalPages} pageSizeOptions={[10, 20, 50]}
                onPageChange={(ev) => this.handlePageChange(ev)}
                style={{display:"flex", marginLeft: "auto"}}>
              <div slot="rows-per-page"><OwcTypography style={{fontSize:"0.75em"}}>Rows per Page</OwcTypography></div>
              <div><OwcTypography style={{fontSize:"0.75em"}}> {specPage} of {totalPages} </OwcTypography></div>
            </OwcPagination>
          : ""
        }
      </div>
    );
  }

  saveFile = async (blob, filename) => {
    const a = document.createElement('a');
    a.download = filename;
    a.href = URL.createObjectURL(blob);
    a.addEventListener('click', () => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
  };

  getResultsText() {
    if (this.state.reportResults === null) return "";
    return JSON.stringify(this.state.reportResults, null, 2);
  }

  downloadJSON() {
    const specSuffix = this.state.reportSpecName ? "-" + this.state.reportSpecName.replaceAll(" ", "-") : "";
    const fileName = ManagementReport.getDataProductName().replaceAll(" ", "-") + specSuffix + ".json";
    const blob = new Blob([this.getResultsText()], { type: "application/json" });
    this.saveFile(blob, fileName);
  }

  downloadCSV() {
    const results = this.state.reportResults;
    if (results === null) return;

    const data = results?.coverageWithDataLicenses?.data;
    const rawData = data?.find(item => item.rawData)?.rawData || [];

    if (rawData.length === 0) return;

    // Flatten one level of nested objects so values like devices/locationAccounts
    // each get their own column (e.g. "Devices Accepted", "Location Accounts Total").
    const titleCase = (key) =>
      key.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()).trim();

    const flattenRow = (row) => {
      const flat = {};
      for (const [key, val] of Object.entries(row)) {
        if (val !== null && typeof val === "object" && !Array.isArray(val)) {
          for (const [subKey, subVal] of Object.entries(val)) {
            flat[`${titleCase(key)} ${subKey}`] = subVal;
          }
        } else {
          flat[titleCase(key)] = val;
        }
      }
      return flat;
    };

    const flatRows = rawData.map(flattenRow);

    // Build a stable, ordered header list across all rows.
    const headers = [];
    for (const row of flatRows) {
      for (const key of Object.keys(row)) {
        if (!headers.includes(key)) headers.push(key);
      }
    }

    const escape = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === "object") return `"${JSON.stringify(val).replace(/"/g, '""')}"`;
      return `"${String(val).replace(/"/g, '""')}"`;
    };

    const csvRows = [headers.join(",")];
    for (const row of flatRows) {
      csvRows.push(headers.map(h => escape(row[h])).join(","));
    }

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const specSuffix = this.state.reportSpecName ? "-" + this.state.reportSpecName.replaceAll(" ", "-") : "";
    const fileName = ManagementReport.getDataProductName().replaceAll(" ", "-") + specSuffix + ".csv";
    this.saveFile(blob, fileName);
  }

   getResultsData(key) {
    if (!this.state.reportResults) return [];
    const data = this.state.reportResults?.coverageWithDataLicenses?.data;
    if (!data || !Array.isArray(data)) return [];
    const summaryEntry = data.find(item => item.summary);
    if (!summaryEntry || !summaryEntry.summary) return [];
    return summaryEntry.summary[key] || [];
  }

  renderDataTable(data, columns) {
    if (!data || data.length === 0) {
      return <OwcTypography variant="body2" style={{ color: "gray" }}>No data available</OwcTypography>;
    }
    return (
      <OwcTable size="dense" elevated style={{ marginTop: "0.5em" }}>
        <OwcTableHeader sticky>
          {columns.map(col => (
            <OwcTableHeaderCell key={"hdr_" + col.key} resizable>{col.label}</OwcTableHeaderCell>
          ))}
        </OwcTableHeader>
        <OwcTableBody>
          {data.map((row, idx) => (
            <OwcTableRow key={"row_" + idx}>
              {columns.map(col => (
                <OwcTableCell key={"cell_" + idx + "_" + col.key} style={{ wordBreak: "normal" }}>
                  {row[col.key] !== null && row[col.key] !== undefined ? String(row[col.key]) : "â"}
                </OwcTableCell>
              ))}
            </OwcTableRow>
          ))}
        </OwcTableBody>
      </OwcTable>
    );
  }

  renderBarChart(data, columns, title) {
    if (!data || data.length === 0) {
      return <OwcTypography variant="body2" style={{ color: "gray" }}>No data available</OwcTypography>;
    }

    const labelKey = columns[0].key;
    const chartedKeys = ["Accepted", "Declined", "Non Responding"];
    const valueKeys = columns.slice(1).filter(c => chartedKeys.includes(c.key));
    const chartHeader = [columns[0].label, ...valueKeys.map(c => c.label)];
    const chartRows = data.map(row => [
      row[labelKey] || "â",
      ...valueKeys.map(c => Number(row[c.key]) || 0),
    ]);
    const chartData = [chartHeader, ...chartRows];

    return (
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="400px"
        data={chartData}
        options={{
          title: title,
          chartArea: { height: "60%" },
          hAxis: { title: columns[0].label },
          vAxis: { title: "Count", minValue: 0 },
          legend: { position: valueKeys.length > 1 ? "top" : "none" },
          isStacked: true,
        }}
      />
    );
  }

  renderResultsSubSection(measureLabel, options, groupByStateKey, viewTabStateKey, note = "") {
    const activeGroupBy = this.state[groupByStateKey];
    const activeOption = options.find(opt => opt.value === activeGroupBy) || options[0];
    const activeTab = this.state[viewTabStateKey];
    const data = this.getResultsData(activeOption.dataKey);
    const title = measureLabel + " by " + activeOption.toggleLabel;
    const switchChecked = activeGroupBy === options[1].value;

    return (
      <div style={{ marginTop: "1em" }}>
        <div style={{
            display: "flex", flexDirection: "row", alignItems: "center",
            justifyContent: "space-between", flexWrap: "wrap", gap: "1em",
            backgroundColor: "var(--one-color-background-intensity-low)", padding: "0.5em",
            width: "100%", boxSizing: "border-box",
          }}>
          <OwcTypography variant="title6">
            {measureLabel + " by " + activeOption.toggleLabel}
            {note ? " " + note : ""}
          </OwcTypography>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.5em" }}>
            <OwcTypography variant="body2"
                style={{ color: switchChecked ? "gray" : "inherit" }}>
              {options[0].toggleLabel}
            </OwcTypography>
            <OwcSwitch
                checked={switchChecked}
                onValueChange={() => this.setState({
                  [groupByStateKey]: switchChecked ? options[0].value : options[1].value,
                })}>
            </OwcSwitch>
            <OwcTypography variant="body2"
                style={{ color: switchChecked ? "inherit" : "gray" }}>
              {options[1].toggleLabel}
            </OwcTypography>
          </div>
        </div>
        <OwcTabs value={activeTab}
            onValueChange={(ev) => this.setState({ [viewTabStateKey]: ev.detail })}>
          <OwcTab value="table">Table</OwcTab>
          <OwcTab value="chart">Chart</OwcTab>
        </OwcTabs>
        <div style={{ marginTop: "0.5em" }}>
          {activeTab === "table"
            ? this.renderDataTable(data, activeOption.columns)
            : this.renderBarChart(data, activeOption.columns, title)
          }
        </div>
      </div>
    );
  }


  renderResultsSection() {
    const hasResults = this.state.reportResults !== null;
    const isGenerating = this.state.isGenerating;
    const generatingMessage = this.state.userMessage?.message || "Generating Data Product...";

    const deviceMeasureColumns = [
      { key: "Accepted", label: "Accepted" },
      { key: "Declined", label: "Declined" },
      { key: "Non Responding", label: "Non Responding" },
      { key: "Total", label: "Total" },
      { key: "Not Enriched", label: "Not Enriched" },
    ];
    const locationAccountMeasureColumns = [
      { key: "Accepted", label: "Accepted" },
      { key: "Declined", label: "Declined" },
      { key: "Non Responding", label: "Non Responding" },
      { key: "Total", label: "Total" },
    ];
    const groupCol = (key, label) => [{ key, label }];

    const devicesGeoOptions = [
      { value: "country", toggleLabel: "Country", dataKey: "devicesByCountry",
        columns: [...groupCol("Country", "Country"), ...deviceMeasureColumns] },
      { value: "region", toggleLabel: "Region", dataKey: "devicesByRegion",
        columns: [...groupCol("Region", "Region"), ...deviceMeasureColumns] },
    ];
    const locationAccountsGeoOptions = [
      { value: "country", toggleLabel: "Country", dataKey: "locationAccountsByCountry",
        columns: [...groupCol("Country", "Country"), ...locationAccountMeasureColumns] },
      { value: "region", toggleLabel: "Region", dataKey: "locationAccountsByRegion",
        columns: [...groupCol("Region", "Region"), ...locationAccountMeasureColumns] },
    ];
    const devicesInstOptions = [
      { value: "line", toggleLabel: "Instrument Line", dataKey: "devicesByInstrumentLine",
        columns: [...groupCol("Instrument Line", "Instrument Line"), ...deviceMeasureColumns] },
      { value: "group", toggleLabel: "Instrument Group", dataKey: "devicesByInstrumentGroup",
        columns: [...groupCol("Instrument Group", "Instrument Group"), ...deviceMeasureColumns] },
    ];
    const locationAccountsInstOptions = [
      { value: "line", toggleLabel: "Instrument Line", dataKey: "locationAccountsByInstrumentLine",
        columns: [...groupCol("Instrument Line", "Instrument Line"), ...locationAccountMeasureColumns] },
      { value: "group", toggleLabel: "Instrument Group", dataKey: "locationAccountsByInstrumentGroup",
        columns: [...groupCol("Instrument Group", "Instrument Group"), ...locationAccountMeasureColumns] },
    ];

    return (
      <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
          expanded={this.state.resultsExpanded}
          onExpandedChange={(ev) => { this.setState({ resultsExpanded: ev.detail }) }}>
        <span slot="title">
          <OwcTypography variant="title6">
            Results{hasResults && this.state.reportSpecName ? ` - ${this.state.reportSpecName} (ID: ${this.state.reportSpecId})` : ""}
          </OwcTypography>
        </span>
        <span slot="content">
          <div style={{ padding: '0.3rem 3rem' }}>
            {isGenerating
              ? <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1em", padding: "2em" }}>
                  <OwcProgressSpinner />
                  <OwcTypography variant="body2" style={{ color: "gray" }}>
                    {generatingMessage}
                  </OwcTypography>
                </div>
              : hasResults
               ? <>
                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "0.5em", marginBottom: "0.5em" }}>
                    <OwcButton size="small"
                        onClick={() => navigator.clipboard.writeText(this.getResultsText())}>
                      Copy to Clipboard
                    </OwcButton>
                    <OwcButton size="small"
                        onClick={() => this.downloadCSV()}>
                      Download CSV
                    </OwcButton>
                    <OwcButton size="small"
                        onClick={() => this.downloadJSON()}>
                      Download JSON
                    </OwcButton>
                  </div>
                  {this.renderResultsSubSection(
                    "Devices", devicesGeoOptions, "devicesGeoGroupBy", "devicesGeoViewTab")}
                  {this.renderResultsSubSection(
                    "Location Accounts", locationAccountsGeoOptions, "locationAccountsGeoGroupBy", "locationAccountsGeoViewTab", "(Location accounts can be double counted)")}
                  {this.renderResultsSubSection(
                    "Devices", devicesInstOptions, "devicesInstGroupBy", "devicesInstViewTab")}
                  {this.renderResultsSubSection(
                    "Location Accounts", locationAccountsInstOptions, "locationAccountsInstGroupBy", "locationAccountsInstViewTab", "(Location accounts can be double counted when grouped)")}
                </>
              : <OwcTypography variant="body2" style={{color: "gray"}}>
                  No results to display. Select a specification and generate a report.
                </OwcTypography>
            }
          </div>
        </span>
      </OwcExpandable>
    );
  }
  

  renderActionButtons() {
    const noneSelected = this.state.selectedSpecId === null;
    const isGenerating = this.state.isGenerating;
    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: '2em', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
          <OwcButton style={{ width: "100%" }}
              disabled={noneSelected}
              onclick={() => this.handleEditSpec()}>
            Edit Specifications
          </OwcButton>
          <OwcButton style={{ width: "100%" }}
              onclick={() => this.handleNewSpec()}>
            New Specifications
          </OwcButton>
          <OwcButton style={{ width: "100%" }}
              variant="warning"
              disabled={noneSelected}
              onclick={() => this.handleDeleteSpec()}>
            Delete Specifications
          </OwcButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
          <OwcButton
              disabled={noneSelected || isGenerating}
              onclick={() => this.handleGenerateReport()}>
            {isGenerating ? "Generating..." : "Generate Report"}
          </OwcButton>
        </div>
      </div>
    );
  }

  renderMainView() {
    return (
      <div style={{ marginLeft: "2em", marginRight: "2em" }}>
        <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
            expanded={this.state.overviewExpanded}
            onExpandedChange={(ev) => { this.setState({ overviewExpanded: ev.detail }) }}>
          <span slot="title">
            <OwcTypography variant="title6">
              Report Overview
            </OwcTypography>
          </span>
          <span slot="content">
            <div style={{ padding: '0.3rem 3rem' }}>
              {this.renderDescriptionSection()}
            </div>
          </span>
        </OwcExpandable>

        <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
            expanded={this.state.specificationsExpanded}
            onExpandedChange={(ev) => { this.setState({ specificationsExpanded: ev.detail }) }}>
          <span slot="title">
            <OwcTypography variant="title6">
              Saved Specifications
            </OwcTypography>
          </span>
          <span slot="content">
            <div style={{ padding: '0.3rem 3rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '2em' }}>
                <div style={{ flexBasis: "50%" }}>
                  {this.renderSearchSection()}
                  <div style={{ marginTop: "0.5em" }}>
                    {this.renderSpecificationList()}
                  </div>
                </div>
                {this.renderActionButtons()}
              </div>
            </div>
          </span>
        </OwcExpandable>

        {this.renderResultsSection()}
      </div>
    );
  }

  render() {
    if (this.state.currentView === "editSpec") {
      return this.renderSpecEditorView();
    }
    return this.renderMainView();
  }
}

export default ManagementReport;